import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Ban, ShieldOff, Search, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";

export default function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [savingUid, setSavingUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const LIMIT = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT, sort: "created_at", order: "desc" };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const r = await api.get("/admin/users", { params });
      setUsers(r.data.data);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch {
      toast.error(t("common.registration_failed"));
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Debounce search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const updateUser = (uid, updates) => {
    setSavingUid(uid);
    api.patch(`/admin/users/${uid}`, updates)
      .then((r) => {
        setUsers((prev) => prev.map((u) => (u.id === uid ? r.data : u)));
        toast.success(t("admin.user_updated"));
      })
      .catch(() => toast.error(t("common.registration_failed")))
      .finally(() => setSavingUid(null));
  };

  const roleStyles = (role) => {
    if (role === "admin") return "border-volt text-volt";
    if (role === "owner") return "border-cyan-400 text-cyan-400";
    return "border-border/60 text-muted-foreground/80";
  };

  const headers = [t("admin.th.name"), t("admin.th.email"), t("admin.th.role"), t("admin.created"), t("admin.th.actions")];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground hover:text-volt transition-colors mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <div className="label-eyebrow text-volt mb-2">{t("admin.eyebrow")}</div>
        <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-10">{t("admin.tabs.users")}</h1>

        {/* Search + Filter */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("admin.search_placeholder")}
              className="w-full bg-card border border-border ps-10 pe-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-volt outline-none transition-colors"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            className="bg-card border border-border rounded-sm px-3 py-2.5 text-sm text-foreground/80 focus:border-volt outline-none uppercase tracking-wider appearance-none cursor-pointer"
          >
            <option value="">{t("admin.filter_all_roles")}</option>
            <option value="player">{t("admin.role_player")}</option>
            <option value="owner">{t("admin.role_owner")}</option>
            <option value="admin">{t("admin.role_admin")}</option>
          </select>
          <span className="text-xs text-muted-foreground/60">{t("admin.total_items", { total })}</span>
        </div>

        {/* Table */}
        <div className="border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="text-start p-3 text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={headers.length} className="p-6 text-center text-muted-foreground/60 text-xs">{t("common.loading")}</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={headers.length} className="p-6 text-center text-muted-foreground/60 text-xs">{t("admin.no_results")}</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="border-t border-border/30">
                  <td className="p-3">
                    <Link to={`/player/${u.id}`} className="font-semibold hover:text-volt transition-colors">{u.name}</Link>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Link to={`/admin/bookings?user_id=${u.id}`} className="text-[10px] text-muted-foreground/70 hover:text-volt transition-colors">Bookings</Link>
                    </div>
                    {u.disabled && <div className="text-[9px] text-red-400 mt-0.5 uppercase tracking-wider">{t("admin.disabled")}</div>}
                  </td>
                  <td className="p-3 text-muted-foreground/80">
                    <div>{u.email}</div>
                    <div className="text-[9px] text-muted-foreground/60 mt-0.5">
                      {u.email_verified ? <Check className="h-3 w-3 inline text-emerald-400" /> : <X className="h-3 w-3 inline text-red-400" />}
                      {u.email_verified ? t("admin.verified") : t("admin.not_verified")}
                    </div>
                  </td>
                  <td className="p-3">
                    <select
                      value={u.role}
                      onChange={(e) => updateUser(u.id, { role: e.target.value })}
                      disabled={savingUid === u.id}
                      className="bg-background border border-border rounded-sm px-2 py-1.5 text-xs uppercase tracking-wider text-foreground font-bold focus:border-volt outline-none disabled:opacity-50 appearance-none cursor-pointer"
                    >
                      <option value="player">{t("admin.role_player")}</option>
                      <option value="owner">{t("admin.role_owner")}</option>
                      <option value="admin">{t("admin.role_admin")}</option>
                    </select>
                  </td>
                  <td className="p-3 text-muted-foreground/80 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateUser(u.id, { disabled: !u.disabled })}
                        disabled={savingUid === u.id}
                        className={`p-1.5 transition-colors disabled:opacity-30 ${u.disabled ? "text-red-400 hover:text-foreground" : "text-muted-foreground/60 hover:text-emerald-400"}`}
                        title={u.disabled ? t("admin.enable_user") : t("admin.disable_user")}
                      >
                        {u.disabled ? <ShieldOff className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                      </button>
                      {savingUid === u.id && <span className="text-[10px] text-muted-foreground/60 animate-pulse">Saving...</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 text-xs tracking-widest uppercase text-muted-foreground/80 hover:text-foreground transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-3 w-3" /> {t("admin.prev_page")}
            </button>
            <span className="text-xs text-muted-foreground/80">{t("admin.page_of", { page, pages })}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="flex items-center gap-1 text-xs tracking-widest uppercase text-muted-foreground/80 hover:text-foreground transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            >
              {t("admin.next_page")} <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
